<template>
  <v-main>
    <!-- Header Section -->
    <div class="websocket-header pa-4">
      <v-container>
        <div class="d-flex align-center justify-space-between">
          <div>
            <h1 class="display-1 font-weight-light mb-2">
              <v-icon large color="primary" class="mr-3">mdi-access-point-network</v-icon>
              WebSocket Test Center
            </h1>
            <p class="subtitle-1 mb-0 grey--text">
              Real-time testing for all WebSocket channels and events
            </p>
          </div>
          <div class="connection-indicator">
            <v-chip
              large
              :color="statusConfig.color"
              :text-color="statusConfig.textColor"
              class="pa-3"
            >
              <v-icon left>{{ statusConfig.icon }}</v-icon>
              {{ connectionStatus }}
            </v-chip>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="pa-6">
      <!-- Statistics Dashboard -->
      <v-row class="mb-4">
        <v-col cols="12" md="3">
          <v-card class="text-center elevation-2">
            <v-card-text>
              <v-icon size="40" color="blue" class="mb-2">mdi-connection</v-icon>
              <div class="headline font-weight-bold">{{ connectionStatus }}</div>
              <div class="caption grey--text">Connection Status</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="text-center elevation-2">
            <v-card-text>
              <v-icon size="40" color="green" class="mb-2">mdi-radio-tower</v-icon>
              <div class="headline font-weight-bold">{{ activeChannels.length }}</div>
              <div class="caption grey--text">Active Channels</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="text-center elevation-2">
            <v-card-text>
              <v-icon size="40" color="orange" class="mb-2">mdi-flash</v-icon>
              <div class="headline font-weight-bold">{{ eventLogs.length }}</div>
              <div class="caption grey--text">Total Events</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card class="text-center elevation-2">
            <v-card-text>
              <v-icon size="40" color="purple" class="mb-2">mdi-account-circle</v-icon>
              <div class="headline font-weight-bold">{{ user ? user.user_name || user.email : 'Guest' }}</div>
              <div class="caption grey--text">Current User</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Left Panel - Controls -->
        <v-col cols="12" lg="4">
            <!-- Connection Control Panel -->
            <v-card class="mb-4 elevation-3">
            <v-card-title class="gradient-primary white--text">
              <v-icon left color="white">mdi-wifi-settings</v-icon>
              Connection Control
            </v-card-title>
            <v-card-text class="pa-4">
              <!-- User Info -->
              <div class="info-section mb-4">
                <h3 class="subtitle-1 font-weight-bold mb-3">
                  <v-icon left color="primary">mdi-account-details</v-icon>
                  Session Information
                </h3>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold">User:</td>
                      <td>{{ user ? user.user_name || user.email : "Not logged in" }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Company:</td>
                      <td>{{ user && user.companies && user.companies[0] ? user.companies[0].name : "No company" }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Token:</td>
                      <td>
                        <v-icon small :color="hasToken ? 'green' : 'red'">
                          {{ hasToken ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ hasToken ? "Valid" : "Missing" }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Connection Controls -->
              <div class="text-center">
                <v-btn
                  @click="testConnection"
                  :disabled="isConnecting"
                  :loading="isConnecting"
                  color="success"
                  class="ma-1"
                  block
                  large
                >
                  <v-icon left>mdi-power-plug</v-icon>
                  {{ isConnecting ? "Connecting..." : "Connect WebSocket" }}
                </v-btn>

                <v-btn 
                  @click="disconnect" 
                  color="error" 
                  class="ma-1"
                  block
                  :disabled="!isConnectedStatus"
                >
                  <v-icon left>mdi-power-plug-off</v-icon>
                  Disconnect
                </v-btn>

                <v-btn 
                  @click="clearLogs" 
                  color="warning" 
                  class="ma-1"
                  block
                  outlined
                >
                  <v-icon left>mdi-broom</v-icon>
                  Clear All Logs
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Channel Management -->
          <v-card class="mb-4 elevation-3">
            <v-card-title class="gradient-success white--text">
              <v-icon left color="white">mdi-access-point</v-icon>
              Channel Manager
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="mb-3">
                <h3 class="subtitle-1 font-weight-bold mb-3">
                  <v-icon left color="green">mdi-radio-tower</v-icon>
                  Active Subscriptions ({{ activeChannels.length }})
                </h3>
                
                <div v-if="activeChannels.length === 0" class="text-center grey--text py-4">
                  <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-access-point-off</v-icon>
                  <div>No active channel subscriptions</div>
                  <div class="caption">Connect and subscribe to channels to start testing</div>
                </div>

                <v-list v-else dense>
                  <v-list-item
                    v-for="channel in activeChannels"
                    :key="channel.name"
                    class="px-0"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon :color="channel.status === 'subscribed' ? 'green' : 'orange'">
                        {{ channel.icon }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="subtitle-2">{{ channel.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip
                        x-small
                        :color="channel.status === 'subscribed' ? 'green' : 'orange'"
                        text-color="white"
                      >
                        {{ channel.status }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="text-center">
                <v-btn
                  @click="subscribeToAllChannels"
                  color="success"
                  class="ma-1"
                  block
                  :disabled="!isConnectedStatus"
                  :loading="subscribingToChannels"
                >
                  <v-icon left>mdi-plus-circle</v-icon>
                  {{ subscribingToChannels ? 'Subscribing...' : 'Subscribe to All Channels' }}
                </v-btn>

                <v-btn
                  @click="unsubscribeFromAllChannels"
                  color="error"
                  class="ma-1"
                  block
                  outlined
                >
                  <v-icon left>mdi-minus-circle</v-icon>
                  Unsubscribe All
                </v-btn>

                <v-btn
                  @click="verifyAllChannels"
                  color="info"
                  class="ma-1"
                  block
                  outlined
                  :loading="verifyingChannels"
                >
                  <v-icon left>mdi-shield-check</v-icon>
                  Verify All Channels
                </v-btn>
              </div>

              <!-- Channel Status Monitor -->
              <v-divider class="my-4"></v-divider>
              <div class="mb-3">
                <h3 class="subtitle-1 font-weight-bold mb-3">
                  <v-icon left color="info">mdi-monitor</v-icon>
                  Channel Status Monitor
                </h3>
                
                <div v-if="channelStatusReport.length === 0" class="text-center grey--text py-2">
                  <v-icon color="grey lighten-1" class="mb-1">mdi-information-outline</v-icon>
                  <div class="caption">Subscribe to channels to see status</div>
                </div>

                <v-list v-else dense class="py-0">
                  <v-list-item
                    v-for="status in channelStatusReport"
                    :key="status.channel"
                    class="px-0 py-1"
                  >
                    <v-list-item-icon class="mr-2 my-1">
                      <v-icon 
                        small 
                        :color="status.isHealthy ? 'green' : 'red'"
                      >
                        {{ status.isHealthy ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="py-1">
                      <v-list-item-title class="caption font-weight-bold">
                        {{ status.channel }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption grey--text">
                        {{ status.status }} • {{ status.lastEventTime || 'No events yet' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-card-text>
          </v-card>

          <!-- Event Testing Panel -->
          <v-card class="elevation-3">
            <v-card-title class="gradient-info white--text">
              <v-icon left color="white">mdi-flask</v-icon>
              Event Testing Lab
            </v-card-title>
            <v-card-text class="pa-4">
              <!-- Wallet Tests -->
              <v-expansion-panels accordion multiple class="mb-3">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left color="orange">mdi-wallet</v-icon>
                      <strong>Wallet Events</strong>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="pt-3">
                      <v-btn
                        @click="triggerWalletWarning"
                        color="orange"
                        class="mb-2"
                        block
                        :loading="testingEvents.wallet"
                      >
                        <v-icon left>mdi-alert</v-icon>
                        Low Balance Warning
                      </v-btn>

                      <v-btn
                        @click="triggerWalletCritical"
                        color="red"
                        class="mb-2"
                        block
                        :loading="testingEvents.walletCritical"
                      >
                        <v-icon left>mdi-alert-circle</v-icon>
                        Critical Balance Alert
                      </v-btn>
                      
                      <v-alert
                        dense
                        text
                        type="info"
                        class="mb-0 mt-2"
                      >
                        Tests wallet balance warnings during consultation billing
                      </v-alert>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left color="blue">mdi-medical-bag</v-icon>
                      <strong>Consultation Events</strong>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="pt-3">
                      <v-btn
                        @click="triggerConsultationStart"
                        color="success"
                        class="mb-2"
                        block
                        :loading="testingEvents.consultationStart"
                      >
                        <v-icon left>mdi-play-circle</v-icon>
                        Start Consultation
                      </v-btn>

                      <v-btn
                        @click="triggerConsultationEnd"
                        color="error"
                        class="mb-2"
                        block
                        :loading="testingEvents.consultationEnd"
                      >
                        <v-icon left>mdi-stop-circle</v-icon>
                        End Consultation
                      </v-btn>

                      <v-alert
                        dense
                        text
                        type="info"
                        class="mb-0 mt-2"
                      >
                        Tests participant join/leave and status updates
                      </v-alert>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left color="green">mdi-chat</v-icon>
                      <strong>Chat Events</strong>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="pt-3">
                      <v-text-field
                        v-model="testMessage"
                        label="Custom Test Message"
                        placeholder="Enter your test message..."
                        outlined
                        dense
                        class="mb-2"
                      ></v-text-field>

                      <v-btn
                        @click="triggerChatMessage"
                        color="primary"
                        class="mb-2"
                        block
                        :loading="testingEvents.chat"
                      >
                        <v-icon left>mdi-send</v-icon>
                        Send Chat Message
                      </v-btn>

                      <v-alert
                        dense
                        text
                        type="info"
                        class="mb-0 mt-2"
                      >
                        Tests real-time chat message broadcasting
                      </v-alert>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Panel - Event Monitor -->
        <v-col cols="12" lg="8">
          <v-card class="elevation-3" style="min-height: 600px;">
            <v-card-title class="gradient-purple white--text">
              <v-icon left color="white">mdi-monitor-dashboard</v-icon>
              Live Event Monitor
              <v-spacer></v-spacer>
              <v-chip
                :color="isConnectedStatus ? 'green' : 'red'"
                text-color="white"
                class="mr-2"
              >
                <v-icon left small>{{ isConnectedStatus ? 'mdi-pulse' : 'mdi-pulse' }}</v-icon>
                {{ eventLogs.length }} Events
              </v-chip>
              <v-btn
                icon
                color="white"
                @click="autoScrollEvents = !autoScrollEvents"
                :class="{ 'green lighten-1': autoScrollEvents }"
              >
                <v-icon>{{ autoScrollEvents ? 'mdi-arrow-down-bold-circle' : 'mdi-arrow-down-bold-circle-outline' }}</v-icon>
              </v-btn>
            </v-card-title>
            
            <!-- Event Controls -->
            <v-card-subtitle class="pb-0">
              <v-row class="align-center">
                <!-- Event Type Filters -->
                <v-col cols="12" md="8">
                  <v-chip-group
                    v-model="selectedFilters"
                    multiple
                    column
                    class="ma-0"
                  >
                    <v-chip
                      v-for="filter in eventFilters"
                      :key="filter.type"
                      :color="filter.color"
                      text-color="white"
                      small
                      filter
                      :value="filter.type"
                    >
                      <v-icon left small>{{ getFilterIcon(filter.type) }}</v-icon>
                      {{ filter.label }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
                
                <!-- Controls -->
                <v-col cols="12" md="4" class="text-right">
                  <v-text-field
                    v-model="eventSearchTerm"
                    prepend-inner-icon="mdi-magnify"
                    label="Search events..."
                    hide-details
                    dense
                    outlined
                    clearable
                    class="mr-2"
                    style="max-width: 200px; display: inline-block;"
                  ></v-text-field>
                  
                  <v-btn
                    icon
                    color="error"
                    @click="clearEventLogs"
                    title="Clear all events"
                  >
                    <v-icon>mdi-delete-sweep</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <!-- Event Log Container -->
            <v-card-text class="pa-0" style="height: 500px;">
              <div
                ref="eventLogContainer"
                class="event-log-container"
                style="height: 100%; overflow-y: auto; padding: 16px;"
              >
                <!-- No Events State -->
                <div
                  v-if="filteredAndSearchedEventLogs.length === 0 && eventLogs.length === 0"
                  class="text-center grey--text pa-8"
                  style="height: 100%; display: flex; flex-direction: column; justify-content: center;"
                >
                  <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-console-line</v-icon>
                  <h3 class="headline font-weight-light mb-2">No Events Yet</h3>
                  <p>Connect to WebSocket and trigger some test events to see real-time data here.</p>
                  <v-btn
                    v-if="!isConnectedStatus"
                    color="primary"
                    @click="testConnection"
                    class="mt-3"
                  >
                    <v-icon left>mdi-power-plug</v-icon>
                    Connect Now
                  </v-btn>
                </div>

                <!-- Filtered Empty State -->
                <div
                  v-else-if="filteredAndSearchedEventLogs.length === 0"
                  class="text-center grey--text pa-4"
                  style="height: 100%; display: flex; flex-direction: column; justify-content: center;"
                >
                  <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-filter-off</v-icon>
                  <p>No events match your current filters or search.</p>
                  <v-btn
                    small
                    @click="clearFiltersAndSearch"
                    color="primary"
                    outlined
                  >
                    Clear Filters
                  </v-btn>
                </div>

                <!-- Event List -->
                <v-timeline
                  v-else
                  dense
                  class="mt-2"
                >
                  <v-timeline-item
                    v-for="(event, index) in filteredAndSearchedEventLogs"
                    :key="index"
                    :color="getEventTypeColor(event.type)"
                    small
                    class="mb-3"
                  >
                    <template v-slot:icon>
                      <v-icon small color="white">{{ getEventIcon(event.type) }}</v-icon>
                    </template>
                    
                    <v-card class="elevation-2 event-card" :class="`border-${getEventTypeColor(event.type)}`">
                      <v-card-text class="pb-2">
                        <!-- Event Header -->
                        <div class="d-flex justify-space-between align-start mb-2">
                          <div>
                            <v-chip
                              x-small
                              :color="getEventTypeColor(event.type)"
                              text-color="white"
                            >
                              {{ event.type.toUpperCase() }}
                            </v-chip>
                            <span class="font-weight-bold ml-2">{{ event.eventName }}</span>
                          </div>
                          <div class="text-right">
                            <div class="caption grey--text">{{ event.time }}</div>
                            <div class="d-flex align-center mt-1">
                              <v-icon small class="mr-1" :color="getChannelStatusColor(event.channel)">
                                {{ getChannelStatusIcon(event.channel) }}
                              </v-icon>
                              <v-chip x-small outlined :color="getChannelStatusColor(event.channel)">
                                {{ event.channel }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Event Message -->
                        <p class="mb-2 body-2">{{ event.message }}</p>
                        
                        <!-- Channel Verification -->
                        <div v-if="event.channelInfo" class="channel-verification-section mb-3">
                          <v-divider class="mb-2"></v-divider>
                          <div class="d-flex justify-space-between align-center mb-2">
                            <span class="caption font-weight-bold">Channel Verification:</span>
                            <v-chip 
                              x-small 
                              :color="event.channelInfo.isSubscribed ? 'green' : 'red'"
                              text-color="white"
                            >
                              <v-icon left x-small>
                                {{ event.channelInfo.isSubscribed ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                              </v-icon>
                              {{ event.channelInfo.isSubscribed ? 'Subscribed' : 'Not Subscribed' }}
                            </v-chip>
                          </div>
                          <v-simple-table dense class="channel-info-table">
                            <tbody>
                              <tr>
                                <td class="caption font-weight-bold">Expected Channel:</td>
                                <td class="caption">{{ event.channelInfo.expected || event.channel }}</td>
                              </tr>
                              <tr>
                                <td class="caption font-weight-bold">Actual Channel:</td>
                                <td class="caption">{{ event.channel }}</td>
                              </tr>
                              <tr>
                                <td class="caption font-weight-bold">Channel Type:</td>
                                <td class="caption">{{ event.channelInfo.type || 'Unknown' }}</td>
                              </tr>
                              <tr v-if="event.channelInfo.userId">
                                <td class="caption font-weight-bold">User ID:</td>
                                <td class="caption">{{ event.channelInfo.userId }}</td>
                              </tr>
                              <tr v-if="event.channelInfo.consultationId">
                                <td class="caption font-weight-bold">Consultation ID:</td>
                                <td class="caption">{{ event.channelInfo.consultationId }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                        
                        <!-- Event Data -->
                        <v-expansion-panels
                          v-if="event.data && Object.keys(event.data).length > 0"
                          flat
                          accordion
                        >
                          <v-expansion-panel>
                            <v-expansion-panel-header class="pa-0 min-height-auto">
                              <template v-slot:default="{ open }">
                                <v-btn
                                  x-small
                                  text
                                  :color="getEventTypeColor(event.type)"
                                >
                                  <v-icon left x-small>{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                  Event Data
                                </v-btn>
                              </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="pa-0">
                              <v-card flat color="grey lighten-5" class="mt-2">
                                <v-card-text class="pa-2">
                                  <pre class="caption event-data-pre">{{ JSON.stringify(event.data, null, 2) }}</pre>
                                </v-card-text>
                              </v-card>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Configuration Panel (Collapsible) -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left>mdi-cog</v-icon>
                WebSocket Configuration & Environment
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <h4>WebSocket Config</h4>
                    <pre class="config-pre">{{
                      JSON.stringify(config, null, 2)
                    }}</pre>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h4>Environment Variables</h4>
                    <pre class="config-pre">{{
                      JSON.stringify(envVars, null, 2)
                    }}</pre>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import WebSocketService from "@/services/websocket";
import axios from "@/axios";

export default {
  name: "ComprehensiveWebSocketTest",
  data() {
    return {
      connectionStatus: "Disconnected",
      isConnecting: false,
      config: {},
      eventLogs: [],
      activeChannels: [],
      eventSearchTerm: "",
      selectedFilters: ["wallet", "consultation", "chat", "billing", "notification", "connection"],
      autoScrollEvents: true,
      testMessage: "Hello from WebSocket test!",
      testingEvents: {
        wallet: false,
        walletCritical: false,
        consultationStart: false,
        consultationEnd: false,
        chat: false
      },
      verifyingChannels: false,
      subscribingToChannels: false,
      channelStatusReport: [],
      eventFilters: [
        { type: "wallet", label: "Wallet", color: "orange" },
        { type: "consultation", label: "Consultation", color: "blue" },
        { type: "chat", label: "Chat", color: "green" },
        { type: "billing", label: "Billing", color: "purple" },
        { type: "notification", label: "Notification", color: "indigo" },
        { type: "connection", label: "Connection", color: "grey" },
      ],
      channels: [
        {
          name: "user",
          type: "private",
          template: "chat.{userId}",
          events: ["message", "typing", "notification"],
          icon: "mdi-account",
          status: "unsubscribed",
        },
        {
          name: "consultation",
          type: "private",
          template: "consultation.{consultationId}",
          events: ["started", "ended", "billing.minute", "billing.stopped"],
          icon: "mdi-medical-bag",
          status: "unsubscribed",
        },
        {
          name: "wallet",
          type: "private",
          template: "wallet.{userId}",
          events: ["low.warning", "transaction", "balance.updated"],
          icon: "mdi-wallet",
          status: "unsubscribed",
        },
        {
          name: "billing",
          type: "private",
          template: "billing.{userId}",
          events: ["started", "processed", "stopped", "failed"],
          icon: "mdi-currency-inr",
          status: "unsubscribed",
        },
        {
          name: "announcement",
          type: "public",
          template: "announcement",
          events: ["new", "updated", "deleted"],
          icon: "mdi-bullhorn",
          status: "unsubscribed",
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    hasToken() {
      return !!this.$store.getters["auth/token"];
    },
    isConnectedStatus() {
      return this.connectionStatus === "Connected";
    },
    statusConfig() {
      switch (this.connectionStatus) {
        case "Connected":
          return {
            color: "green",
            textColor: "white",
            icon: "mdi-check-circle",
          };
        case "Connecting":
          return { color: "orange", textColor: "white", icon: "mdi-loading" };
        case "Error":
          return { color: "red", textColor: "white", icon: "mdi-alert-circle" };
        default:
          return {
            color: "grey",
            textColor: "white",
            icon: "mdi-minus-circle",
          };
      }
    },
    envVars() {
      return {
        VUE_APP_API_URL: process.env.VUE_APP_API_URL,
        VUE_APP_WEBSOCKET_HOST: process.env.VUE_APP_WEBSOCKET_HOST,
        VUE_APP_WEBSOCKET_PORT: process.env.VUE_APP_WEBSOCKET_PORT,
        VUE_APP_WEBSOCKET_KEY: process.env.VUE_APP_WEBSOCKET_KEY,
        VUE_APP_WEBSOCKET_SCHEME: process.env.VUE_APP_WEBSOCKET_SCHEME,
        NODE_ENV: process.env.NODE_ENV,
      };
    },
    filteredEventLogs() {
      return this.eventLogs.filter((event) =>
        this.selectedFilters.includes(event.type)
      );
    },
    filteredAndSearchedEventLogs() {
      let filtered = this.filteredEventLogs;
      
      if (this.eventSearchTerm) {
        const searchTerm = this.eventSearchTerm.toLowerCase();
        filtered = filtered.filter(event =>
          event.eventName.toLowerCase().includes(searchTerm) ||
          event.message.toLowerCase().includes(searchTerm) ||
          event.channel.toLowerCase().includes(searchTerm) ||
          event.type.toLowerCase().includes(searchTerm)
        );
      }
      
      return filtered;
    },
  },
  mounted() {
    this.setupConfig();
    this.addEventLog(
      "connection",
      "system",
      "WebSocket Test",
      "Component mounted and ready",
      {}
    );
  },
  methods: {
    setupConfig() {
      const token =
        this.$store.getters["auth/token"] || localStorage.getItem("token");
      this.config = {
        broadcaster: "pusher",
        key: process.env.VUE_APP_WEBSOCKET_KEY || "healthy-tantra-key",
        wsHost: process.env.VUE_APP_WEBSOCKET_HOST || "127.0.0.1",
        wsPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        wssPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        forceTLS: process.env.VUE_APP_WEBSOCKET_SCHEME === "wss",
        encrypted: process.env.VUE_APP_WEBSOCKET_SCHEME === "wss",
        cluster: "eu",
        authEndpoint: `${
          process.env.VUE_APP_API_URL || "http://127.0.0.1:8000"
        }/api/broadcasting/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      };
    },

    async testConnection() {
      this.isConnecting = true;
      this.connectionStatus = "Connecting";
      this.addEventLog(
        "connection",
        "system",
        "Connection Test",
        "Attempting to connect to WebSocket...",
        {}
      );

      try {
        if (!this.hasToken) {
          throw new Error("No authentication token available");
        }

        // Disconnect any existing connection first
        WebSocketService.disconnect();
        
        const echo = WebSocketService.connect();

        if (echo) {
          this.addEventLog(
            "connection",
            "system",
            "Echo Instance",
            "WebSocket service returned echo instance",
            {}
          );

          // Setup connection event listeners
          echo.connector.pusher.connection.bind("connected", () => {
            this.connectionStatus = "Connected";
            this.addEventLog(
              "connection",
              "pusher",
              "Connected",
              "WebSocket connection established successfully",
              {}
            );
          });

          echo.connector.pusher.connection.bind("disconnected", () => {
            this.connectionStatus = "Disconnected";
            this.addEventLog(
              "connection",
              "pusher",
              "Disconnected",
              "WebSocket connection lost",
              {}
            );
            this.activeChannels.forEach(
              (channel) => (channel.status = "unsubscribed")
            );
          });

          echo.connector.pusher.connection.bind("error", (error) => {
            this.connectionStatus = "Error";
            this.addEventLog(
              "connection",
              "pusher",
              "Connection Error",
              error.message || "Unknown error",
              { error }
            );
          });

          // Wait for connection
          setTimeout(() => {
            const isConnected = WebSocketService.isConnected();
            if (isConnected) {
              this.connectionStatus = "Connected";
              this.addEventLog(
                "connection",
                "system",
                "Status Check",
                "Connection verified successfully",
                {}
              );
            } else {
              this.connectionStatus = "Error";
              this.addEventLog(
                "connection",
                "system",
                "Status Check",
                "Connection verification failed",
                {}
              );
            }
          }, 2000);
        } else {
          throw new Error("WebSocket service failed to return echo instance");
        }
      } catch (error) {
        this.connectionStatus = "Error";
        this.addEventLog(
          "connection",
          "system",
          "Connection Error",
          error.message,
          { error: error.toString() }
        );
      }

      this.isConnecting = false;
    },

    disconnect() {
      WebSocketService.disconnect();
      this.connectionStatus = "Disconnected";
      this.activeChannels.forEach(
        (channel) => (channel.status = "unsubscribed")
      );
      this.addEventLog(
        "connection",
        "system",
        "Disconnect",
        "WebSocket disconnected manually",
        {}
      );
    },

    async subscribeToAllChannels() {
      this.subscribingToChannels = true;
      
      try {
        // Validate prerequisites
        if (!this.isConnectedStatus) {
          this.addEventLog(
            "connection",
            "system",
            "Subscription Error",
            "Cannot subscribe - WebSocket not connected. Please connect first.",
            { required_action: "Connect WebSocket first" }
          );
          return;
        }

        if (!this.user) {
          this.addEventLog(
            "connection",
            "system",
            "Subscription Error", 
            "Cannot subscribe - no authenticated user found",
            { required_action: "User authentication required" }
          );
          return;
        }

        // Check if Echo instance is available
        const echo = WebSocketService.getEcho();
        if (!echo) {
          this.addEventLog(
            "connection",
            "system",
            "Subscription Error",
            "Echo instance not available - please reconnect WebSocket",
            { required_action: "Reconnect WebSocket" }
          );
          return;
        }

      console.log(`🔄 Starting subscription to ${this.channels.length} channels for user ${this.user.id}`);
      
      this.addEventLog(
        "connection",
        "system",
        "Bulk Subscription Started",
        `Starting subscription to ${this.channels.length} channels`,
        { 
          user_id: this.user.id,
          channels: this.channels.map(c => c.name),
          timestamp: new Date().toISOString()
        }
      );

      let successCount = 0;
      let errorCount = 0;

      // Subscribe to each channel with delay to prevent overwhelming
      for (let i = 0; i < this.channels.length; i++) {
        const channelConfig = this.channels[i];
        
        try {
          console.log(`🔄 Subscribing to channel ${i + 1}/${this.channels.length}: ${channelConfig.name}`);
          
          const channel = this.subscribeToChannel(channelConfig);
          
          if (channel) {
            successCount++;
          } else {
            errorCount++;
          }
          
          // Small delay between subscriptions to prevent overwhelming the server
          if (i < this.channels.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
        } catch (error) {
          console.error(`❌ Failed to subscribe to ${channelConfig.name}:`, error);
          errorCount++;
        }
      }

      // Summary log
      this.addEventLog(
        "connection",
        "system", 
        "Bulk Subscription Complete",
        `Subscription complete: ${successCount} successful, ${errorCount} failed`,
        {
          success_count: successCount,
          error_count: errorCount,
          total_channels: this.channels.length,
          timestamp: new Date().toISOString()
        }
      );

        // Update channel status report
        this.updateChannelStatusReport();

        console.log(`✅ Subscription complete: ${successCount}/${this.channels.length} channels subscribed successfully`);
        
      } catch (generalError) {
        console.error('❌ Unexpected error during bulk subscription:', generalError);
        this.addEventLog(
          "connection",
          "system",
          "Subscription Error",
          `Unexpected error during bulk subscription: ${generalError.message}`,
          { error: generalError.toString() }
        );
      } finally {
        this.subscribingToChannels = false;
      }
    },

    subscribeToChannel(channelConfig) {
      try {
        let channelName = channelConfig.template;

        // Replace placeholders
        if (channelName.includes("{userId}") && this.user) {
          channelName = channelName.replace("{userId}", this.user.id);
        }
        if (channelName.includes("{consultationId}")) {
          channelName = channelName.replace(
            "{consultationId}",
            "test-consultation-1"
          );
        }

        // Get the Echo instance correctly
        const echo = WebSocketService.getEcho();
        if (!echo) {
          throw new Error("No echo instance available - WebSocket not connected");
        }

        console.log(`🔄 Subscribing to channel: ${channelName} (type: ${channelConfig.type})`);

        let channel;
        
        // Use appropriate channel subscription method based on type
        if (channelConfig.type === "private") {
          channel = echo.private(channelName);
          console.log(`✅ Subscribed to private channel: ${channelName}`);
        } else if (channelConfig.type === "presence") {
          channel = echo.join(channelName);
          console.log(`✅ Subscribed to presence channel: ${channelName}`);
        } else {
          channel = echo.channel(channelName);
          console.log(`✅ Subscribed to public channel: ${channelName}`);
        }

        // Setup event listeners for this channel
        console.log(`🔄 Setting up event listeners for ${channelName}:`, channelConfig.events);
        
        channelConfig.events.forEach((eventName) => {
          const fullEventName = eventName.startsWith('.') ? eventName : `.${eventName}`;
          
          channel.listen(fullEventName, (data) => {
            console.log(`📨 Event received on ${channelName}:`, eventName, data);
            // Enhanced logging with detailed channel information
            const eventMessage = `Received ${eventName} event on channel ${channelName}`;
            this.addEventLog(
              channelConfig.name,
              channelName,
              eventName,
              eventMessage,
              {
                ...data,
                _channel_info: {
                  subscription_channel: channelName,
                  event_name: eventName,
                  channel_type: channelConfig.type,
                  received_at: new Date().toISOString()
                }
              }
            );
          });
          
          console.log(`✅ Listening for event: ${fullEventName} on ${channelName}`);
        });

        // Handle subscription success/error events
        if (channelConfig.type === "private") {
          channel.subscribed(() => {
            console.log(`✅ Successfully subscribed to private channel: ${channelName}`);
            this.addEventLog(
              channelConfig.name,
              channelName,
              "Subscription Confirmed",
              `Private channel subscription confirmed: ${channelName}`,
              { timestamp: new Date().toISOString() }
            );
          });

          channel.error((error) => {
            console.error(`❌ Subscription error for ${channelName}:`, error);
            this.addEventLog(
              "connection",
              channelName,
              "Subscription Error",
              `Failed to authenticate for private channel: ${channelName}`,
              { error: error.toString() }
            );
          });
        }

        // Update channel status
        const activeChannel = this.activeChannels.find(
          (c) => c.name === channelName
        );
        if (activeChannel) {
          activeChannel.status = "subscribed";
        } else {
          this.activeChannels.push({
            name: channelName,
            icon: channelConfig.icon,
            status: "subscribed",
            type: channelConfig.type,
            events: channelConfig.events
          });
        }

        this.addEventLog(
          channelConfig.name,
          channelName,
          "Subscribed",
          `Successfully subscribed to ${channelName}`,
          { 
            channel_type: channelConfig.type,
            events: channelConfig.events,
            timestamp: new Date().toISOString()
          }
        );

        return channel;

      } catch (error) {
        console.error(`❌ Failed to subscribe to ${channelConfig.name}:`, error);
        this.addEventLog(
          "connection",
          "system",
          "Subscription Error",
          `Failed to subscribe to ${channelConfig.name}: ${error.message}`,
          { error: error.toString() }
        );
        return null;
      }
    },

    unsubscribeFromAllChannels() {
      this.activeChannels.forEach((channel) => {
        try {
          // WebSocket service handles unsubscription
          channel.status = "unsubscribed";
        } catch (error) {
          console.error("Error unsubscribing:", error);
        }
      });
      this.activeChannels = [];
      this.channelStatusReport = [];
      this.addEventLog(
        "connection",
        "system",
        "Unsubscribed",
        "Unsubscribed from all channels",
        {}
      );
    },

    async verifyAllChannels() {
      this.verifyingChannels = true;
      
      try {
        // Update channel status report
        this.updateChannelStatusReport();
        
        // Test each active channel by sending a ping or checking status
        for (const channel of this.activeChannels) {
          await this.verifyChannel(channel);
        }
        
        this.addEventLog(
          "connection",
          "system",
          "Channel Verification",
          `Verified ${this.activeChannels.length} channels`,
          {
            channels: this.activeChannels.map(c => ({
              name: c.name,
              status: c.status,
              verified: true
            }))
          }
        );
      } catch (error) {
        this.addEventLog(
          "connection",
          "system",
          "Verification Error",
          `Failed to verify channels: ${error.message}`,
          { error: error.toString() }
        );
      } finally {
        this.verifyingChannels = false;
      }
    },

    async verifyChannel(channel) {
      return new Promise((resolve) => {
        // Simulate channel verification - in real implementation,
        // you might send a ping or check connection status
        setTimeout(() => {
          const isHealthy = Math.random() > 0.1; // 90% success rate for demo
          channel.lastVerified = new Date().toISOString();
          channel.isHealthy = isHealthy;
          resolve(isHealthy);
        }, 100);
      });
    },

    updateChannelStatusReport() {
      this.channelStatusReport = this.activeChannels.map(channel => {
        const recentEvents = this.eventLogs.filter(event => 
          event.channel === channel.name && 
          event.type !== 'connection'
        ).slice(0, 5);
        
        return {
          channel: channel.name,
          status: channel.status,
          isHealthy: channel.status === 'subscribed',
          eventCount: recentEvents.length,
          lastEventTime: recentEvents.length > 0 ? recentEvents[0].time : null,
          lastEventType: recentEvents.length > 0 ? recentEvents[0].type : null
        };
      });
    },

    // Test Event Triggers
    async triggerWalletWarning() {
      this.testingEvents.wallet = true;
      try {
        const response = await axios.post("/test/trigger-wallet-warning", {
          user_id: this.user?.id,
          warning_type: "warning",
        });
        this.addEventLog(
          "wallet",
          "trigger",
          "Wallet Warning",
          "Triggered wallet warning test",
          response.data
        );
      } catch (error) {
        this.addEventLog(
          "wallet",
          "trigger",
          "Trigger Error",
          "Failed to trigger wallet warning",
          { error: error.message }
        );
      } finally {
        this.testingEvents.wallet = false;
      }
    },

    async triggerWalletCritical() {
      this.testingEvents.walletCritical = true;
      try {
        const response = await axios.post("/test/trigger-wallet-warning", {
          user_id: this.user?.id,
          warning_type: "critical",
        });
        this.addEventLog(
          "wallet",
          "trigger",
          "Critical Warning",
          "Triggered critical wallet warning test",
          response.data
        );
      } catch (error) {
        this.addEventLog(
          "wallet",
          "trigger",
          "Trigger Error",
          "Failed to trigger critical warning",
          { error: error.message }
        );
      } finally {
        this.testingEvents.walletCritical = false;
      }
    },

    async triggerConsultationStart() {
      this.testingEvents.consultationStart = true;
      try {
        const response = await axios.post("/test/trigger-consultation-start", {
          doctor_id: this.user?.id,
          patient_id: this.user?.id,
        });
        this.addEventLog(
          "consultation",
          "trigger",
          "Start Consultation",
          "Triggered consultation start test",
          response.data
        );
      } catch (error) {
        this.addEventLog(
          "consultation",
          "trigger",
          "Trigger Error",
          "Failed to trigger consultation start",
          { error: error.message }
        );
      } finally {
        this.testingEvents.consultationStart = false;
      }
    },

    async triggerConsultationEnd() {
      this.testingEvents.consultationEnd = true;
      try {
        const response = await axios.post("/test/trigger-consultation-end", {
          consultation_id: "test-consultation-1",
        });
        this.addEventLog(
          "consultation",
          "trigger",
          "End Consultation",
          "Triggered consultation end test",
          response.data
        );
      } catch (error) {
        this.addEventLog(
          "consultation",
          "trigger",
          "Trigger Error",
          "Failed to trigger consultation end",
          { error: error.message }
        );
      } finally {
        this.testingEvents.consultationEnd = false;
      }
    },

    async triggerChatMessage() {
      this.testingEvents.chat = true;
      try {
        const response = await axios.post("/test/trigger-chat-message", {
          user_id: this.user?.id,
          message: this.testMessage || "Test WebSocket message from Vue frontend",
        });
        this.addEventLog(
          "chat",
          "trigger",
          "Chat Message",
          "Triggered test chat message",
          response.data
        );
      } catch (error) {
        this.addEventLog(
          "chat",
          "trigger",
          "Trigger Error",
          "Failed to trigger chat message",
          { error: error.message }
        );
      } finally {
        this.testingEvents.chat = false;
      }
    },

    // Utility Methods
    addEventLog(type, channel, eventName, message, data, includeChannelInfo = true) {
      const eventLog = {
        type,
        channel,
        eventName,
        message,
        data,
        time: new Date().toLocaleTimeString(),
      };

      // Add channel verification information
      if (includeChannelInfo && this.user && type !== 'connection') {
        const userId = this.user.id;
        const consultationId = data && data.consultation_id ? data.consultation_id : null;
        eventLog.channelInfo = this.createChannelInfo(type, channel, userId, consultationId);
      }

      this.eventLogs.unshift(eventLog);

      // Update channel status report when we receive events
      if (type !== 'connection' && this.activeChannels.length > 0) {
        this.updateChannelStatusReport();
      }

      // Keep only last 100 events
      if (this.eventLogs.length > 100) {
        this.eventLogs = this.eventLogs.slice(0, 100);
      }

      // Auto-scroll to latest event
      if (this.autoScrollEvents) {
        this.$nextTick(() => {
          if (this.$refs.eventLogContainer) {
            this.$refs.eventLogContainer.scrollTop = 0;
          }
        });
      }
    },

    clearEventLogs() {
      this.eventLogs = [];
    },

    clearLogs() {
      this.clearEventLogs();
    },

    clearFiltersAndSearch() {
      this.selectedFilters = ["wallet", "consultation", "chat", "billing", "notification", "connection"];
      this.eventSearchTerm = "";
    },

    getEventTypeColor(type) {
      const filter = this.eventFilters.find((f) => f.type === type);
      return filter ? filter.color : "grey";
    },

    getEventIcon(type) {
      const iconMap = {
        wallet: 'mdi-wallet',
        consultation: 'mdi-medical-bag', 
        chat: 'mdi-message-text',
        billing: 'mdi-currency-inr',
        notification: 'mdi-bell',
        connection: 'mdi-wifi'
      };
      return iconMap[type] || 'mdi-circle';
    },

    getFilterIcon(type) {
      const iconMap = {
        wallet: 'mdi-wallet',
        consultation: 'mdi-medical-bag',
        chat: 'mdi-chat',
        billing: 'mdi-currency-inr', 
        notification: 'mdi-bell',
        connection: 'mdi-wifi'
      };
      return iconMap[type] || 'mdi-circle';
    },

    getChannelStatusColor(channelName) {
      const isSubscribed = this.activeChannels.some(channel => 
        channel.name === channelName && channel.status === 'subscribed'
      );
      return isSubscribed ? 'green' : 'orange';
    },

    getChannelStatusIcon(channelName) {
      const isSubscribed = this.activeChannels.some(channel => 
        channel.name === channelName && channel.status === 'subscribed'
      );
      return isSubscribed ? 'mdi-check-circle' : 'mdi-alert-circle';
    },

    getExpectedChannelName(type, userId, consultationId = null) {
      switch (type) {
        case 'wallet':
          return `wallet.${userId}`;
        case 'chat':
          return `chat.${userId}`;
        case 'consultation':
          return consultationId ? `consultation.${consultationId}` : `consultation.test-${Date.now()}`;
        case 'billing':
          return `billing.${userId}`;
        case 'notification':
          return 'announcement';
        default:
          return 'unknown';
      }
    },

    createChannelInfo(type, actualChannel, userId, consultationId = null) {
      const expectedChannel = this.getExpectedChannelName(type, userId, consultationId);
      const isSubscribed = this.activeChannels.some(channel => 
        channel.name === actualChannel && channel.status === 'subscribed'
      );

      return {
        expected: expectedChannel,
        actual: actualChannel,
        type: this.getChannelType(actualChannel),
        isSubscribed,
        userId,
        consultationId,
        matches: expectedChannel === actualChannel
      };
    },

    getChannelType(channelName) {
      if (channelName.startsWith('chat.')) return 'Private User Chat';
      if (channelName.startsWith('wallet.')) return 'Private Wallet';
      if (channelName.startsWith('consultation.')) return 'Private Consultation';
      if (channelName.startsWith('billing.')) return 'Private Billing';
      if (channelName === 'announcement') return 'Public Announcement';
      return 'Unknown';
    },
  },
};
</script>

<style scoped>
/* Header Styling */
.websocket-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Gradient Backgrounds */
.gradient-primary {
  background: linear-gradient(45deg, #1976d2, #42a5f5) !important;
}

.gradient-success {
  background: linear-gradient(45deg, #388e3c, #66bb6a) !important;
}

.gradient-info {
  background: linear-gradient(45deg, #0277bd, #29b6f6) !important;
}

.gradient-purple {
  background: linear-gradient(45deg, #7b1fa2, #ab47bc) !important;
}

/* Configuration Pre */
.config-pre {
  font-size: 11px;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  max-height: 250px;
  overflow: auto;
  border: 1px solid #e3f2fd;
  font-family: 'Roboto Mono', monospace;
}

/* Event Cards */
.event-card {
  transition: all 0.2s ease;
  border-left: 4px solid transparent !important;
}

.event-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.border-orange .event-card {
  border-left-color: #ff9800 !important;
}

.border-blue .event-card {
  border-left-color: #2196f3 !important;
}

.border-green .event-card {
  border-left-color: #4caf50 !important;
}

.border-purple .event-card {
  border-left-color: #9c27b0 !important;
}

.border-indigo .event-card {
  border-left-color: #3f51b5 !important;
}

.border-grey .event-card {
  border-left-color: #607d8b !important;
}

/* Event Data Pre */
.event-data-pre {
  font-family: 'Roboto Mono', monospace;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
  max-height: 200px;
  overflow: auto;
  border: 1px solid #e9ecef;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Info Section */
.info-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e9ecef;
}

/* Custom Scrollbar */
.event-log-container::-webkit-scrollbar {
  width: 10px;
}

.event-log-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.event-log-container::-webkit-scrollbar-thumb {
  background: linear-gradient(45deg, #667eea, #764ba2);
  border-radius: 10px;
}

.event-log-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(45deg, #5a6fd8, #6a4190);
}

/* Card Elevations */
.elevation-3 {
  box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23) !important;
}

/* Timeline Customization */
.v-timeline--dense .v-timeline-item__divider {
  margin: 12px 0;
}

/* Expansion Panel Customization */
.v-expansion-panel-header {
  min-height: 40px !important;
}

.v-expansion-panel-content__wrap {
  padding: 8px 12px 12px !important;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .websocket-header h1 {
    font-size: 2rem !important;
  }
  
  .connection-indicator .v-chip {
    display: none;
  }
}

/* Animation for loading buttons */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.v-btn--loading {
  animation: pulse 2s infinite;
}

/* Custom chip styling */
.v-chip--filter.v-chip--active:before {
  opacity: 0.2;
}

/* Card hover effects */
.v-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.v-card:hover {
  transform: translateY(-1px);
}

/* Text selection styling */
::selection {
  background: #667eea;
  color: white;
}

::-moz-selection {
  background: #667eea;
  color: white;
}

/* Channel Verification Styling */
.channel-verification-section {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e9ecef;
}

.channel-info-table {
  background: white;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.channel-info-table tbody tr {
  height: 28px;
}

.channel-info-table tbody tr:nth-child(odd) {
  background-color: #f8f9fa;
}

.channel-info-table tbody tr td {
  padding: 6px 8px !important;
  font-size: 11px !important;
  line-height: 1.2;
}

/* Channel status indicators */
.channel-status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
}

/* Enhanced event cards with channel status */
.event-card .v-card__text {
  padding-bottom: 8px !important;
}

/* Event Log Container Styling */
.event-log-container {
  background: transparent;
}

/* Responsive design improvements */
@media (max-width: 1264px) {
  .v-card {
    margin-bottom: 16px;
  }
}
</style>